import { useState, useEffect, useRef } from 'react';
import { useAuth } from './firebase/useAuth';
import { AppProvider, useApp } from './context/AppContext';
import SignInScreen from './screens/SignInScreen';
import TodayScreen from './screens/TodayScreen';
import JournalScreen from './screens/JournalScreen';
import FeedScreen from './screens/FeedScreen';
import FriendsScreen from './screens/FriendsScreen';
import JoinScreen from './screens/JoinScreen';
import ResponseDetailScreen from './screens/ResponseDetailScreen';
import BottomNav from './components/BottomNav';
import styles from './App.module.css';

function AppShell() {
  const { user } = useAuth();
  const { state, dispatch } = useApp();

  const isPopStateNav = useRef(false);
  const prevTabRef = useRef(state.activeTab);
  const prevDetailRef = useRef(state.activeDetail);

  // Seed the current history entry so there is always a state to read on popstate
  useEffect(() => {
    history.replaceState({ type: 'tab', tab: state.activeTab }, '');
  }, []); // intentionally runs once on mount

  // Push a history entry whenever the active tab changes (user-initiated only)
  useEffect(() => {
    if (state.activeTab !== prevTabRef.current) {
      prevTabRef.current = state.activeTab;
      if (!isPopStateNav.current) {
        history.pushState({ type: 'tab', tab: state.activeTab }, '');
      }
    }
  }, [state.activeTab]);

  // Push a history entry whenever the detail overlay opens (user-initiated only)
  useEffect(() => {
    const hadDetail = Boolean(prevDetailRef.current);
    const hasDetail = Boolean(state.activeDetail);
    if (hasDetail && !hadDetail && !isPopStateNav.current) {
      history.pushState({ type: 'detail' }, '');
    }
    prevDetailRef.current = state.activeDetail;
  }, [state.activeDetail]);

  // Handle the phone / browser back gesture
  useEffect(() => {
    const handlePopState = (e) => {
      isPopStateNav.current = true;
      if (state.activeDetail) {
        dispatch({ type: 'CLOSE_DETAIL' });
      } else if (e.state?.type === 'tab' && e.state.tab) {
        dispatch({ type: 'SET_ACTIVE_TAB', tab: e.state.tab });
      }
      setTimeout(() => { isPopStateNav.current = false; }, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [state.activeDetail, dispatch]);

  const [joinShareId] = useState(() => {
    const match = window.location.pathname.match(/^\/join\/([A-Za-z0-9]+)$/);
    return match ? match[1] : null;
  });

  const [activeJoinId, setActiveJoinId] = useState(joinShareId);

  const [feedPersonFilter, setFeedPersonFilter] = useState(null);

  const setTab = (tab) => dispatch({ type: 'SET_ACTIVE_TAB', tab });

  const clearJoin = () => {
    window.history.replaceState('', '', '/');
    setActiveJoinId(null);
  };

  const handleJoinComplete = () => {
    clearJoin();
    setTab('feed');
  };

  if (activeJoinId) {
    return (
      <JoinScreen
        shareId={activeJoinId}
        user={user}
        onComplete={handleJoinComplete}
        onCancel={clearJoin}
      />
    );
  }

  if (!user) return <SignInScreen />;

  const handleViewFriendFeed = (userId) => {
    setFeedPersonFilter(userId);
    setTab('feed');
  };

  const todayIncomplete = !state.loading && (!state.todayEntry || state.todayEntry.isDraft);

  return (
    <div className={styles.shell}>
      {state.activeTab === 'today'   && <TodayScreen />}
      {state.activeTab === 'journal' && <JournalScreen />}
      {state.activeTab === 'feed'    && <FeedScreen initialPersonFilter={feedPersonFilter} />}
      {state.activeTab === 'friends' && (
        <FriendsScreen onViewFriendFeed={handleViewFriendFeed} />
      )}
      <BottomNav
        activeTab={state.activeTab}
        onTabChange={setTab}
        showTodayDot={todayIncomplete}
      />
      {state.activeDetail && <ResponseDetailScreen />}
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppShell />
    </AppProvider>
  );
}
